import React, { useRef, useState, useEffect } from "react";
import { useMoodleV2 } from "../../contexts/moodle";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useConfig } from "../../contexts/config";
import { getProgressTextColors } from "./textColors";
import { requestAutoLogin } from "../../services/moodle/userauth";
import { getCourseCertificate } from "../../services/moodle/courses";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DownloadIcon from "@mui/icons-material/Download";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

export const ProgressCardItem = ({
  course,
  grade,
  progress,
  lang,
  certificateCmid,
  rowIndex,
  onRowClick,
}) => {
  const moodlev2 = useMoodleV2();
  const theme = useTheme();
  const textColors = getProgressTextColors(theme.mode);
  const isDark = theme.mode === "dark";
  const { user } = useAuth();
  const { config } = useConfig();

  const [downloadStatus, setDownloadStatus] = useState("idle");
  const successTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
      }
    };
  }, []);

  const startDate = course.startdate
    ? new Date(course.startdate * 1000).toLocaleDateString()
    : "-";
  const endDate =
    course.enddate && course.enddate !== 0
      ? new Date(course.enddate * 1000).toLocaleDateString()
      : "Sin fecha de término";

  const handleRowClick = () => {
    if (onRowClick) onRowClick(course);
  };

  const handleCertificateDownload = async (e) => {
    e.stopPropagation();
    if (!certificateCmid || downloadStatus !== "idle" || moodlev2.isDownloading) return;

    setDownloadStatus("loading");
    moodlev2.setIsDownloading(true);

    try {
      const res = await getCourseCertificate({
        token: user.token,
        cmid: certificateCmid,
        userid: user.id || 0,
        baseUrl: config.baseURL,
      });

      if (res && res.filecontent) {
        const byteCharacters = atob(res.filecontent);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });
        const blobUrl = window.URL.createObjectURL(blob);

        const downloadLink = document.createElement("a");
        downloadLink.href = blobUrl;
        downloadLink.download = res.filename || `Certificado_${course.displayname || "curso"}.pdf`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        window.URL.revokeObjectURL(blobUrl);

        setDownloadStatus("success");
        successTimerRef.current = setTimeout(() => {
          setDownloadStatus("idle");
        }, 2000);
        return;
      }
    } catch (err) {
      console.warn("Direct certificate download fallback:", err.message);
      try {
        let { key } = await requestAutoLogin({
          username: user.username,
          baseUrl: config.baseURL,
          tipddytoken: config.token,
        });
        const url = `https://api-interfaz-alumnos.tipddy.cl/history/cert/${certificateCmid}?userid=${user.id}&domain=${encodeURIComponent(config.baseURL)}&userkey=${key}`;
        window.open(url, "_blank", "noopener,noreferrer");
      } catch (fallbackErr) {
        console.error("Fallback download failed:", fallbackErr);
      }
      setDownloadStatus("idle");
    } finally {
      moodlev2.setIsDownloading(false);
    }
  };

  const progNum = progress !== undefined && progress !== null ? Math.floor(progress) : null;
  const isCompleted = progNum !== null && progNum >= 100;

  return (
    <div
      onClick={handleRowClick}
      className="flex flex-col p-4 rounded-xl cursor-pointer transition-all duration-150 active:scale-[0.99]"
      style={{
        backgroundColor: isDark ? "rgba(255, 255, 255, 0.03)" : "#FFFFFF",
        border: "1.5px solid rgba(236, 178, 54, 0.4)",
        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.15)",
        fontFamily: "'Montserrat', sans-serif",
      }}
    >
      {/* Course Title + Chevron */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <h3
          className="font-bold text-base leading-snug m-0"
          style={{ color: textColors.primary }}
        >
          {course.displayname || course.fullname}
        </h3>
        <ChevronRightIcon
          sx={{ fontSize: 22, color: "#ecb236", shrink: 0, marginTop: "2px" }}
        />
      </div>

      {/* Metrics Row: Progress & Grade */}
      <div
        className="flex items-center justify-between py-2.5 my-1"
        style={{
          borderTop: `1px solid ${isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)"}`,
          borderBottom: `1px solid ${isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)"}`,
        }}
      >
        {/* Progress */}
        <div className="flex items-center gap-2">
          <span
            className="text-xs font-medium"
            style={{ color: textColors.secondary }}
          >
            Progreso:
          </span>
          <div className="flex items-center gap-1">
            {isCompleted ? (
              <CheckCircleIcon sx={{ fontSize: 16, color: "#22c55e" }} />
            ) : null}
            <span
              className="font-bold text-sm"
              style={{ color: isCompleted ? "#22c55e" : "#ecb236" }}
            >
              {progNum !== null ? `${progNum}%` : "-"}
            </span>
          </div>
        </div>

        {/* Grade */}
        <div className="flex items-center gap-2">
          <span
            className="text-xs font-medium"
            style={{ color: textColors.secondary }}
          >
            Nota final:
          </span>
          <span
            className="font-bold text-sm"
            style={{ color: textColors.primary }}
          >
            {grade !== "-" ? grade : "-"}
          </span>
        </div>
      </div>

      {/* Footer Row: Dates & Certificate */}
      <div className="flex items-center justify-between text-xs pt-1.5 gap-2">
        <div className="flex flex-col text-[11px]" style={{ color: textColors.secondary }}>
          <span>Inicio: {startDate}</span>
          {endDate !== "Sin fecha de término" && <span>Fin: {endDate}</span>}
        </div>

        {certificateCmid ? (
          <button
            type="button"
            disabled={downloadStatus !== "idle" || moodlev2.isDownloading}
            onClick={handleCertificateDownload}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all"
            style={{
              backgroundColor:
                downloadStatus === "success"
                  ? "rgba(34, 197, 94, 0.2)"
                  : "rgba(236, 178, 54, 0.15)",
              color: downloadStatus === "success" ? "#22c55e" : "#ecb236",
              border: `1px solid ${downloadStatus === "success" ? "rgba(34, 197, 94, 0.4)" : "rgba(236, 178, 54, 0.4)"}`,
            }}
          >
            {downloadStatus === "loading" ? (
              <>
                <CircularProgress size={12} thickness={5} sx={{ color: "#ecb236" }} />
                <span>Generando...</span>
              </>
            ) : downloadStatus === "success" ? (
              <>
                <CheckCircleIcon sx={{ fontSize: 14 }} />
                <span>Listo</span>
              </>
            ) : (
              <>
                <DownloadIcon sx={{ fontSize: 14 }} />
                <span>Certificado</span>
              </>
            )}
          </button>
        ) : null}
      </div>
    </div>
  );
};
